// src/prototype/components/oir/report/OIRSectionStrategicContext.tsx

import React from 'react';
import { Target, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface OIRSectionStrategicContextProps {
  clientName?: string;
  personalizationData?: {
    profile: number;
    surveys: number;
    documents: number;
    initiatives: number;
  };
  strategicPriorities?: string[];
  knownConstraints?: string[];
}

// Strength label + color from overall %
const getStrengthLabel = (pct: number): { label: string; color: string } => {
  if (pct >= 75) return { label: 'Strong', color: 'text-emerald-600' };
  if (pct >= 50) return { label: 'Good', color: 'text-[#18365E]' };
  if (pct >= 30) return { label: 'Partial', color: 'text-amber-600' };
  return { label: 'Limited', color: 'text-rose-600' };
};

const EmptyListState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#F5F7FA] border border-[#e5e7eb]">
    <span className="text-xs text-[#717182] italic">{message}</span>
  </div>
);

export const OIRSectionStrategicContext: React.FC<OIRSectionStrategicContextProps> = ({
  clientName = 'Your Organization',
  personalizationData = { profile: 0, surveys: 0, documents: 0, initiatives: 0 },
  strategicPriorities,
  knownConstraints,
}) => {
  const { profile, surveys, documents, initiatives } = personalizationData;
  const overall = Math.round((profile + surveys + documents + initiatives) / 4);
  const { label: strengthLabel, color: strengthColor } = getStrengthLabel(overall);

  const hasPriorities = strategicPriorities && strategicPriorities.length > 0;
  const hasConstraints = knownConstraints && knownConstraints.length > 0;

  const dimensions = [
    { label: 'Organization Profile', value: profile },
    { label: 'Survey Responses', value: surveys },
    { label: 'Uploaded Documents', value: documents },
    { label: 'Prior Initiatives', value: initiatives },
  ];

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 mb-5">
      {/* Section header */}
      <div className="mb-4">
        <h2 className="text-base font-bold text-[#18365E]">Strategic Context</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">
          Organizational profile, priorities, and known constraints shaping this analysis
        </p>
      </div>

      {/* Narrative block */}
      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-5">
        The analysis is calibrated to <strong>{clientName}</strong>'s organizational profile,
        strategic priorities, and known constraints. Opportunity rankings reflect the depth and
        specificity of inputs provided — higher personalization strength yields tighter alignment
        between surfaced initiatives and institutional goals.
      </p>

      {/* 2-column: priorities + constraints */}
      <div className="grid grid-cols-1 gap-4 mb-5 sm:grid-cols-2">
        {/* Left — Strategic Priorities */}
        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <Target className="w-3.5 h-3.5 text-[#18365E]" />
            <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
              Strategic Priorities
            </p>
          </div>
          {hasPriorities ? (
            <div className="flex flex-col gap-1.5">
              {strategicPriorities.map((p) => (
                <div key={p} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#FF6E4C] shrink-0 mt-0.5" />
                  <span className="text-xs text-[#18365E] leading-snug">{p}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyListState message="No strategic priorities on record" />
          )}
        </div>

        {/* Right — Known Constraints */}
        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
              Known Constraints
            </p>
          </div>
          {hasConstraints ? (
            <div className="flex flex-col gap-2">
              {knownConstraints.map((c) => (
                <div
                  key={c}
                  className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2"
                >
                  <p className="text-xs text-amber-800 leading-snug">{c}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyListState message="No known constraints on record" />
          )}
        </div>
      </div>

      {/* Personalization strength callout */}
      <div className="bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
            Personalization Strength
          </p>
          <span className={`text-xs font-bold ${strengthColor}`}>
            {strengthLabel} ({overall}%)
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {dimensions.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[#717182]">{label}</span>
                <span className="text-[10px] font-semibold text-[#18365E]">{value}%</span>
              </div>
              <div className="h-1.5 bg-[#e5e7eb] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#18365E] rounded-full transition-all"
                  style={{ width: `${value}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};